import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  telemetryService, classifyTelemetryError,
  TelemetryEvent, TelemetryFailure, TelemetryResponse,
} from '../../../services/telemetryService';
import { DeviceDetailCore } from '../useDeviceDetail';

const DEFAULT_LIMIT = 500;

/** Today as YYYY-MM-DD in the browser's zone. */
export const todayIso = (): string => toIsoDay(new Date());

export const toIsoDay = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const shiftIsoDay = (iso: string, days: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return toIsoDay(new Date(y, m - 1, d + days));
};

/**
 * The Monitoring panel: one day of the events a device reported, read
 * through the backend from the Elasticsearch index the MQTT apps write.
 * Loads when the panel is first shown and whenever the day or the event
 * code changes while it is shown; a tab switch keeps what was loaded.
 */
export const useDevicesDetailsMonitoring = (core: DeviceDetailCore, active: boolean) => {
  const { device, isOpen } = core;
  const [date, setDate] = useState<string>(todayIso);
  const [evt, setEvt] = useState<TelemetryEvent>('report');
  const [data, setData] = useState<TelemetryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<TelemetryFailure | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  // A new device or a re-open starts from today, with nothing loaded.
  useEffect(() => {
    if (!isOpen) return;
    setDate(todayIso());
    setEvt('report');
    setData(null);
    setFailure(null);
    setLoadedKey(null);
  }, [isOpen, device?.idDevice]);

  const key = `${device?.idDevice ?? ''}|${evt}|${date}`;
  // The key of the request in flight: a response for another key (the
  // user moved on) is dropped, and the same key is never fetched twice.
  const inFlight = useRef<string | null>(null);

  useEffect(() => {
    const deviceId = device?.idDevice;
    if (!active || !deviceId || key === loadedKey || inFlight.current === key) return;
    inFlight.current = key;
    setLoading(true);
    setFailure(null);
    telemetryService.getDeviceEvents(deviceId, { evt, date, limit: DEFAULT_LIMIT })
      .then((response) => {
        if (inFlight.current === key) setData(response);
      })
      .catch((err) => {
        if (inFlight.current !== key) return;
        console.error('Failed to load telemetry:', err);
        setData(null);
        setFailure(classifyTelemetryError(err));
      })
      .finally(() => {
        if (inFlight.current !== key) return;
        inFlight.current = null;
        setLoadedKey(key);
        setLoading(false);
      });
  }, [active, device?.idDevice, evt, date, key, loadedKey]);

  const refresh = useCallback(() => setLoadedKey(null), []);
  const today = todayIso();
  const isToday = date === today;
  const goPreviousDay = useCallback(() => setDate((d) => shiftIsoDay(d, -1)), []);
  const goNextDay = useCallback(() => setDate((d) => (d < today ? shiftIsoDay(d, 1) : d)), [today]);
  const goToday = useCallback(() => setDate(today), [today]);
  const pickDate = useCallback((iso: string) => {
    if (iso && iso <= today) setDate(iso);
  }, [today]);

  // Items arrive newest first; the summary reads both ends. What else it
  // says depends on the kind of event and is computed by the panel.
  const summary = useMemo(() => {
    if (!data || data.items.length === 0) return null;
    const newest = data.items[0];
    const oldest = data.items[data.items.length - 1];
    return {
      count: data.items.length,
      total: data.total,
      truncated: data.truncated,
      firstAt: oldest.at,
      lastAt: newest.at,
    };
  }, [data]);

  return {
    date, evt, setEvt, data, loading, failure, summary,
    today, isToday, goPreviousDay, goNextDay, goToday, pickDate, refresh,
  };
};
